/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import useTagsState from './hooks/useTagState.js';
import TagModalComponent from './components/TagModal.js';

var ResourceTagModal = function ResourceTagModal(_ref) {
  var open = _ref.open,
    onClose = _ref.onClose,
    onSave = _ref.onSave,
    mock = _ref.mock,
    resourceId = _ref.resourceId,
    accountId = _ref.accountId,
    supportAccessTags = _ref.supportAccessTags,
    tagAreaTooltipDirection = _ref.tagAreaTooltipDirection;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useTagsState = useTagsState(accountId, mock, open, resourceId, supportAccessTags, t),
    state = _useTagsState.state,
    resource = _useTagsState.resource,
    userTags = _useTagsState.userTags,
    accessTags = _useTagsState.accessTags,
    errorMessage = _useTagsState.errorMessage,
    accessTagPermissions = _useTagsState.accessTagPermissions,
    userTagPermissions = _useTagsState.userTagPermissions;
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    saveErrorMessage = _useState2[0],
    setSaveErrorMessage = _useState2[1];
  var ACCESS_TAGS_LIMIT_EXCEEDED = 'MAX_NUMBER_ACCESS_TAGS_EXCEEDED';
  var INVALID_TAG_NAME = 'INVALID_TAG_NAME';
  var geti18nErrorStrings = function geti18nErrorStrings(errors) {
    var i18nErrors = [];
    errors.forEach(function (errObj) {
      var i18nMessage = errObj.message;
      var _errObj$response = errObj.response,
        response = _errObj$response === void 0 ? '' : _errObj$response;
      if (response === ACCESS_TAGS_LIMIT_EXCEEDED) {
        i18nMessage = t("You've reached the maximum number of tags that you can attach at a single time. To attach more tags, save your changes first.");
      } else if (response === INVALID_TAG_NAME) {
        i18nMessage = t("Tag '{{tagName}}' could not be created as the name is invalid.", {
          tagName: errObj.tag_name
        });
      }
      i18nErrors.push(_objectSpread2(_objectSpread2({}, errObj), {}, {
        message: i18nMessage
      }));
    });
    return i18nErrors;
  };
  var handleOnSave = function handleOnSave(args) {
    var _saveErrors$;
    var _args$attached = args.attached,
      attached = _args$attached === void 0 ? [] : _args$attached,
      _args$detached = args.detached,
      detached = _args$detached === void 0 ? [] : _args$detached,
      success = args.success,
      errors = args.errors;
    // Refetch all tags, we want the entire list of attached tags.
    var tags = _toConsumableArray(resource.tags);
    detached.forEach(function (item) {
      var tag = item.tag;
      var index = tags.indexOf(tag.name);
      tags.splice(index, 1);
    });
    attached.forEach(function (item) {
      var tag = item.tag;
      tags.push(tag.name);
    });
    var saveErrors = (errors === null || errors === void 0 ? void 0 : errors.length) > 0 ? geti18nErrorStrings(errors) : errors;
    setSaveErrorMessage(saveErrors === null || saveErrors === void 0 || (_saveErrors$ = saveErrors[0]) === null || _saveErrors$ === void 0 || (_saveErrors$ = _saveErrors$.error) === null || _saveErrors$ === void 0 ? void 0 : _saveErrors$.message);
    onSave({
      attachedTags: attached,
      detachedTags: detached,
      errors: saveErrors,
      success: success,
      tags: tags
    });
  };
  var handleOnClose = function handleOnClose() {
    onClose();
  };
  var anyErrorMessage = errorMessage || saveErrorMessage;
  return /*#__PURE__*/React.createElement("div", null, open ? /*#__PURE__*/React.createElement(TagModalComponent, {
    open: open,
    labelColon: false,
    mock: mock,
    onSave: handleOnSave,
    onClose: handleOnClose,
    isLoading: state !== 'TAGS_LOADED',
    resource: resource,
    tags: userTags || [],
    accessTags: accessTags || [],
    accessTagPermissions: accessTagPermissions,
    userTagPermissions: userTagPermissions,
    error: anyErrorMessage ? {
      message: anyErrorMessage
    } : undefined,
    t: t,
    tagAreaTooltipDirection: tagAreaTooltipDirection
  }) : null);
};
ResourceTagModal.propTypes = {
  /**
   * The ID for the resource. Either a CRN of the softlayer ID.
   */
  resourceId: PropTypes.string.isRequired,
  /**
   * Whether the access tags UI should be displayed or not.
   */
  supportAccessTags: PropTypes.bool,
  /**
   * The account ID for the user. Used to determine tags permissions if supportAccessTags is enabled.
   */
  accountId: PropTypes.string,
  /**
   * A function to call when the modal is closed.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * A function to call when the save button is clicked.
   */
  onSave: PropTypes.func.isRequired,
  /**
   * Whether or not the modal should be displayed or not.
   */
  open: PropTypes.bool.isRequired,
  /**
   * Whether or not we should use mock data to populate the modal.
   */
  mock: PropTypes.bool,
  /**
   * The location of the TagArea tooltip, relative to the trigger UI.
   */
  tagAreaTooltipDirection: PropTypes.oneOf(['top', 'top-left', 'top-right', 'bottom', 'bottom-left', 'bottom-right', 'left', 'left-bottom', 'left-top', 'right', 'right-bottom', 'right-top'])
};
ResourceTagModal.defaultProps = {
  mock: false,
  supportAccessTags: false,
  tagAreaTooltipDirection: 'bottom-left',
  accountId: undefined
};
var ResourceTagModal$1 = ResourceTagModal;

export { ResourceTagModal$1 as default };
